import {useCompanyQuery} from "@/api/companyApi";
import CopyIcon from "@/assets/colorable/copy.svg?react";
import GlobeIcon from "@/assets/colorable/globe.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import MailPlusIcon from "@/assets/colorable/mail_plus.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import Button from "@/components/Common/Button";
import CheckBox from "@/components/Common/CheckBox";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton";
import Modal from "@/components/Common/Modal";
import PreviewButton from "@/components/Common/PreviewButton";
import TextButton from "@/components/Common/TextButton";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {hidePublishShareNudgeAtom, inviteAmbassadorsModalAtom, publishShareNudgeModalAtom} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {useAtom, useSetAtom} from "jotai";
import {type FC, useState} from "react";
import {useTranslation} from "react-i18next";

const PublishShareNudgeModal: FC = () => {
  const {t} = useTranslation(["referral", "common"]);
  const [nudge, setNudge] = useAtom(publishShareNudgeModalAtom);
  const [hiddenByUser, setHiddenByUser] = useAtom(hidePublishShareNudgeAtom);
  const setInviteAmbassadorsModal = useSetAtom(inviteAmbassadorsModalAtom);
  const userId = useUserStore(state => state.user.id);
  const {data: company} = useCompanyQuery({companyId: nudge.companyId!});
  const hideNudge = !!hiddenByUser[Number(userId)];
  const [dontShowAgain, setDontShowAgain] = useState(hideNudge);

  const shareLink = company?.slug ? `${window.location.origin}/join/${company.slug}` : "";

  const shareSuggestions = [
    {Icon: InstagramIcon, label: t("publish_nudge_where_instagram")},
    {Icon: TiktokIcon, label: t("publish_nudge_where_tiktok")},
    {Icon: MailPlusIcon, label: t("publish_nudge_where_newsletter")},
    {Icon: GlobeIcon, label: t("publish_nudge_where_website")},
  ];

  const closeModal = () => {
    setHiddenByUser(prev => ({...prev, [Number(userId)]: dontShowAgain}));
    setNudge(state => {
      state.companyId = null;
      state.isOpen = false;
    });
  };

  const dismissModal = (method: "close" | "invite_ambassadors" | "maybe_later") => {
    capturePostHogEvent(PostHogEvents.JoinLinkSharePromptDismissed, {dontShowAgain, method});
    closeModal();
  };

  if (!company || !shareLink) return null;

  return (
    <Modal
      className="gap-4"
      backgroundClassName="max-w-120 w-[90vw]"
      closeButtonClassName="mt-5 self-start"
      customTitleRow={
        <div className="flex flex-col gap-2 p-5">
          <span className="text-neutral-black text-xl font-semibold">🎉 {t("publish_nudge_title")}</span>
          <span className="text-neutral-grey-dark text-sm">{t("publish_nudge_body")}</span>
        </div>
      }
      onClose={() => dismissModal("close")}
      customBottomRow={<></>}>
      {!!shareLink && (
        <div className="flex w-full flex-col gap-1.5">
          <span className="text-neutral-black text-sm font-semibold">{t("publish_nudge_share_hint")}</span>
          <div className="border-neutral-grey-light-dark bg-neutral-grey-lightest flex h-11 items-center overflow-hidden rounded-md border px-3">
            <span className="text-neutral-black truncate text-base font-medium">{shareLink}</span>
          </div>
          <div className="flex items-center gap-2 self-end">
            <CopyToClipboardButton
              buttonStyle="White"
              className="h-8 w-fit gap-2 px-5 text-sm whitespace-nowrap"
              contentToCopy={shareLink}
              icon={<CopyIcon className="size-3.5 shrink-0" />}
              label={t("copy")}
              onCopyCallback={() => capturePostHogEvent(PostHogEvents.JoinLinkCopied)}
            />
            <PreviewButton icon={null} link={shareLink} className="h-8 w-fit text-sm whitespace-nowrap" />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-neutral-grey-dark text-xs">{t("publish_nudge_where_to_share_hint")}</span>
            {shareSuggestions.map(({Icon, label}) => (
              <span key={label} className="text-neutral-grey-dark flex items-center gap-1 text-xs font-medium">
                <Icon className="size-3.5 shrink-0" />
                {label}
              </span>
            ))}
          </div>
        </div>
      )}
      <Button
        buttonStyle="Blue"
        className="h-12 w-full rounded-lg"
        onClick={() => {
          capturePostHogEvent(PostHogEvents.JoinLinkSharePromptClicked);
          const companyId = nudge.companyId;
          dismissModal("invite_ambassadors");
          setInviteAmbassadorsModal(state => {
            state.agencyId = null;
            state.companyId = companyId;
            state.isOpen = true;
            state.openedFromAmbassadorProgram = false;
            state.section = "public";
          });
        }}>
        <span className="font-medium">{t("publish_nudge_share_button")}</span>
      </Button>
      <div className="flex items-center justify-between gap-3">
        <CheckBox
          isChecked={dontShowAgain}
          onChange={setDontShowAgain}
          label={t("publish_nudge_dont_show_again")}
          labelClassName="text-neutral-grey-dark text-sm font-normal"
        />
        <TextButton onClick={() => dismissModal("maybe_later")} text={t("publish_nudge_maybe_later")} />
      </div>
    </Modal>
  );
};

export default PublishShareNudgeModal;
